import {FC, useEffect, useMemo, useState} from "react";
import classNames from "classnames/dedupe";
import {cn} from "src/helpers/bem";
import "./styles.scss";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs, {Dayjs} from "dayjs";
import {BannerProjectsField, ImageUploadField} from "src/components";
import {
  BANNER_PLACEMENTS,
  bannerLabel,
  bannerProjectError,
  extractEmploymentStatuses,
} from "src/consts/banners";
import {
  useBannerMeta,
  useCreateBanner,
  useUpdateBanner,
} from "src/hooks/banners";
import {BannerModel, BannerWriteRequest} from "src/api/generated";

const b = cn("banner-form-dialog");

interface Props {
  className?: string;
  open: boolean;
  banner?: BannerModel | null;
  onClose: () => void;
  onSaved?: (message: string) => void;
}

interface FormState {
  imageUrl: string;
  title: string;
  placements: string[];
  landingUrl: string;
  startsAt: Dayjs | null;
  endsAt: Dayjs | null;
  priority: string;
  dismissible: boolean;
  reappearAfterDays: string;
  isActive: boolean;
  platforms: string[];
  employmentStatuses: string[];
  minAppVersion: string;
  allProjects: boolean;
  projectIds: string[];
}

const emptyForm: FormState = {
  imageUrl: "",
  title: "",
  placements: [],
  landingUrl: "",
  startsAt: null,
  endsAt: null,
  priority: "0",
  dismissible: false,
  reappearAfterDays: "",
  isActive: true,
  platforms: [],
  employmentStatuses: [],
  minAppVersion: "",
  allProjects: true,
  projectIds: [],
};

const bannerToForm = (banner: BannerModel): FormState => ({
  imageUrl: banner.imageUrl ?? "",
  title: banner.title ?? "",
  placements: banner.placements ?? [],
  landingUrl: banner.landingUrl ?? "",
  startsAt: banner.startsAt ? dayjs(banner.startsAt) : null,
  endsAt: banner.endsAt ? dayjs(banner.endsAt) : null,
  priority: String(banner.priority ?? 0),
  dismissible: Boolean(banner.dismissible),
  reappearAfterDays:
    banner.reappearAfterDays != null ? String(banner.reappearAfterDays) : "",
  isActive: Boolean(banner.isActive),
  platforms: banner.audience?.platforms ?? [],
  employmentStatuses: banner.audience?.employmentStatuses ?? [],
  minAppVersion: banner.audience?.minAppVersion ?? "",
  // projectIds приходит как string[] | null. Режим выводим из самого массива, а
  // не из allProjects: так недопустимая комбинация «false + пустой список»
  // недостижима, а старые баннеры без обоих полей читаются как «Все проекты».
  allProjects: (banner.projectIds ?? []).length === 0,
  projectIds: banner.projectIds ?? [],
});

export const BannerFormDialog: FC<Props> = (props) => {
  const {className, open, banner, onClose, onSaved} = props;

  const isEdit = Boolean(banner?.id);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [showErrors, setShowErrors] = useState(false);

  const {data: meta} = useBannerMeta();
  const employmentOptions = useMemo(
    () => extractEmploymentStatuses(meta),
    [meta],
  );

  const createMutation = useCreateBanner();
  const updateMutation = useUpdateBanner();
  const saving = createMutation.isPending || updateMutation.isPending;

  // Инициализация формы при открытии диалога
  useEffect(() => {
    if (open) {
      setForm(banner ? bannerToForm(banner) : emptyForm);
      setShowErrors(false);
      createMutation.reset();
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, banner]);

  const patch = (partial: Partial<FormState>) =>
    setForm((prev) => ({...prev, ...partial}));

  const titleError = !form.title.trim();
  const imageError = !form.imageUrl;
  const placementsError = form.placements.length === 0;
  const hasValidationError = titleError || imageError || placementsError;

  const submitError = createMutation.error ?? updateMutation.error;
  const rawSubmitError = (submitError as any)?.response?.data?.error as
    | string
    | undefined;
  const projectError = bannerProjectError(rawSubmitError);
  const submitErrorText =
    submitError && !projectError
      ? (rawSubmitError ?? "Не удалось сохранить баннер")
      : null;

  const handleSubmit = () => {
    if (hasValidationError) {
      setShowErrors(true);
      return;
    }

    const hasAudience =
      form.platforms.length > 0 ||
      form.employmentStatuses.length > 0 ||
      Boolean(form.minAppVersion);

    const body: BannerWriteRequest = {
      imageUrl: form.imageUrl,
      title: form.title.trim(),
      placements: form.placements,
      landingUrl: form.landingUrl || undefined,
      startsAt: form.startsAt ? form.startsAt.toISOString() : undefined,
      endsAt: form.endsAt ? form.endsAt.toISOString() : undefined,
      priority: form.priority ? Number(form.priority) : undefined,
      
      dismissible: form.dismissible,
      reappearAfterDays:
        form.dismissible && form.reappearAfterDays
          ? Number(form.reappearAfterDays)
          : undefined,
      isActive: form.isActive,
      allProjects: form.allProjects,
      projectIds: form.allProjects ? [] : form.projectIds,
      audience: hasAudience
        ? {
            platforms: form.platforms.length ? form.platforms : undefined,
            employmentStatuses: form.employmentStatuses.length
              ? form.employmentStatuses
              : undefined,
            minAppVersion: form.minAppVersion || undefined,
          }
        : undefined,
    };

    if (isEdit && banner?.id) {
      updateMutation.mutate(
        {id: banner.id, body},
        {
          onSuccess: () => {
            onSaved?.("Баннер обновлён");
            onClose();
          },
        },
      );
    } else {
      createMutation.mutate(body, {
        onSuccess: () => {
          onSaved?.("Баннер создан");
          onClose();
        },
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classNames(b(), className)}
      maxWidth="sm"
    >
      <DialogTitle>
        {isEdit ? "Редактировать баннер" : "Создать баннер"}
      </DialogTitle>

      <DialogContent className={b("content")}>
        <span className={b("field-label")}>Изображение баннера *</span>
        <ImageUploadField
          value={form.imageUrl}
          onChange={(imageUrl) => patch({imageUrl})}
          disabled={saving}
        />
        {showErrors && imageError && (
          <FormHelperText error>Загрузите изображение</FormHelperText>
        )}

        <TextField
          label="Название"
          size="small"
          required
          value={form.title}
          error={showErrors && titleError}
          helperText={showErrors && titleError ? "Обязательное поле" : undefined}
          onChange={(e) => patch({title: e.target.value})}
        />

        <FormControl size="small" required error={showErrors && placementsError}>
          <InputLabel>Размещения</InputLabel>
          <Select
            multiple
            value={form.placements}
            input={<OutlinedInput label="Размещения" />}
            onChange={(e) =>
              patch({placements: e.target.value as string[]})
            }
            renderValue={(selected) => (
              <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={bannerLabel(BANNER_PLACEMENTS, value)}
                    size="small"
                  />
                ))}
              </Box>
            )}
          >
            {BANNER_PLACEMENTS.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.value}
              </MenuItem>
            ))}
          </Select>
          {showErrors && placementsError && (
            <FormHelperText>Выберите хотя бы одно размещение</FormHelperText>
          )}
        </FormControl>

        <TextField
          label="Ссылка при клике"
          size="small"
          value={form.landingUrl}
          onChange={(e) => patch({landingUrl: e.target.value})}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className={b("row")}>
            <DatePicker
              label="Начало показа"
              slotProps={{textField: {size: "small"}}}
              value={form.startsAt}
              onChange={(value) => patch({startsAt: value})}
            />
            <DatePicker
              label="Окончание показа"
              slotProps={{textField: {size: "small"}}}
              value={form.endsAt}
              onChange={(value) => patch({endsAt: value})}
            />
          </div>
        </LocalizationProvider>

        <div className={b("row")}>
          <TextField
            label="Приоритет"
            type="number"
            size="small"
            inputProps={{min: 0}}
            value={form.priority}
            onChange={(e) => patch({priority: e.target.value})}
          />
        </div>

        <div className={b("section-title")}>Аудитория</div>

        <BannerProjectsField
          allProjects={form.allProjects}
          projectIds={form.projectIds}
          initialProjects={banner?.projects}
          onChange={({allProjects, projectIds}) =>
            patch({allProjects, projectIds})
          }
          disabled={saving}
          error={projectError}
        />

        <FormControl size="small">
          <InputLabel>Статусы занятости</InputLabel>
          <Select
            multiple
            value={form.employmentStatuses}
            input={<OutlinedInput label="Статусы занятости" />}
            onChange={(e) =>
              patch({employmentStatuses: e.target.value as string[]})
            }
            renderValue={(selected) => (
              <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={bannerLabel(employmentOptions, value)}
                    size="small"
                  />
                ))}
              </Box>
            )}
          >
            {employmentOptions.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Мин. версия приложения"
          size="small"
          placeholder="например, 2.4.0"
          value={form.minAppVersion}
          onChange={(e) => patch({minAppVersion: e.target.value})}
        />

        <FormControlLabel
          control={
            <Switch
              checked={form.isActive}
              onChange={(_, checked) => patch({isActive: checked})}
            />
          }
          label="Активен"
        />

        {submitErrorText && (
          <Alert severity="error" className={b("error")}>
            {submitErrorText}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Отмена
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {isEdit ? "Сохранить" : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
